import {
  Box,
  createStyles,
  ImageList,
  makeStyles,
  Typography,
} from '@material-ui/core';
import axios, { CancelTokenSource } from 'axios';
import React, { useMemo, useState } from 'react';
import TakenImages from '../../../../models/TakenImages';

// subcomponents
import { UploadStateMap } from '../../../../store/inspectionForm/slice';
import Picture from './Picture';
import PictureDetail from './PictureDetails';

const useStyles = makeStyles(() =>
  createStyles({
    root: {
      width: '100%',
      display: 'flex',
      flexWrap: 'wrap',
      overflow: 'hidden',
      marginTop: 12,
    },
    gridList: {
      flexWrap: 'nowrap',
      transform: 'translateZ(0)',
    },
  }),
);

export interface PictureItem {
  name: string;
  data: string;
}

type Props = {
  images: TakenImages;
  uploadStateMap: UploadStateMap;
  onRetryUpload: (
    identifier: string,
    base64Image: string,
    cancelTokenSource?: CancelTokenSource,
  ) => void;
  onCancelUpload: (uploadStateMap: UploadStateMap) => void;
};

const nameMap: { [key: string]: string } = {
  frontal: 'Frontal',
  back: 'Trasera',
  interior: 'Interior',
  left: 'Izquierda',
  right: 'Derecha',
  windshield: 'Interior(VIN)',
};

const PictureList = ({
  images,
  uploadStateMap,
  onRetryUpload,
  onCancelUpload,
}: Props): JSX.Element => {
  const classes = useStyles();
  const items = Object.entries(images).reverse();

  const [base64ImageSelected, updateBase64ImageSelected] = React.useState('');
  const [imageIdSelected, updateimageIdSelected] = React.useState('');
  const [openPictureDetail, updateOpenPictureDetail] = React.useState(false);

  /** Maintain a map of cancelTokens for each section */
  const [cancelTokenMap, setCancelTokenMap] = useState<{
    [key: string]: CancelTokenSource;
  }>({
    frontal: axios.CancelToken.source(),
    back: axios.CancelToken.source(),
    interior: axios.CancelToken.source(),
    left: axios.CancelToken.source(),
    right: axios.CancelToken.source(),
  });

  /**
   * Returns a valid display name for the given key
   *
   * @param name - The unique key that identifies the photo
   */
  function nameForKey(key: string): string {
    if (Object.keys(nameMap).includes(key)) {
      return nameMap[key];
    }
    return '';
  }

  function captureCancelled(): void {
    updateOpenPictureDetail(false);
    updateBase64ImageSelected('');
    updateimageIdSelected('');
  }

  /**
   * Clicking expands the picture and allows for retake as long as it is not uploading
   */
  function handleRetakePicture(identifier: string, base64Image: string): void {
    if (!['failed', 'uploading'].includes(uploadStateMap[identifier])) {
      // Picture re-take logic
      updateOpenPictureDetail(true);
      updateBase64ImageSelected(base64Image);
      updateimageIdSelected(identifier);
    }
  }

  function handleReupload(identifier: string, base64Image: string): void {
    if (uploadStateMap[identifier] === 'failed') {
      // Upload retry logic
      onRetryUpload(identifier, base64Image, cancelTokenMap[identifier]);
    }
  }

  function handleCancelUpload(
    identifier: string,
    cancelTokenSource: CancelTokenSource,
  ): void {
    cancelTokenSource.cancel();
    onCancelUpload({ [identifier]: 'failed' });

    /** Generate new cancel token for corresponding image */
    setCancelTokenMap({
      ...cancelTokenMap,
      [identifier]: axios.CancelToken.source(),
    });
  }

  const renderPictureDetail = useMemo(
    () => (
      <PictureDetail
        data-testid="picturedetail"
        open={openPictureDetail}
        base64Image={base64ImageSelected}
        imageId={imageIdSelected}
        onCaptureCancelled={captureCancelled}
      />
    ),
    [openPictureDetail, base64ImageSelected, imageIdSelected],
  );

  return (
    <Box px="1rem">
      {renderPictureDetail}
      <Typography variant="body2">Fotos tomadas</Typography>
      <Typography variant="caption">
        Toca la foto para agrandar o tomarla nuevamente
      </Typography>
      <div className={classes.root}>
        <ImageList className={classes.gridList} cols={1}>
          {items.map(
            ([key, value]): JSX.Element => (
              <Picture
                key={`photo-box-${key}`}
                data-testid={`photo-box-${key}`}
                state={uploadStateMap[key]}
                image={value}
                section={nameForKey(key)}
                onRetakeAction={(): void => handleRetakePicture(key, value)}
                onRetryAction={(): void => handleReupload(key, value)}
                onCancelAction={(): void =>
                  handleCancelUpload(key, cancelTokenMap[key])
                }
              />
            ),
          )}
        </ImageList>
      </div>
    </Box>
  );
};

export default PictureList;
